import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, CloudOff, RefreshCw, UploadCloud, WifiOff } from "lucide-react";
import { getQueueSummary } from "../../utils/offlineQueue";
import { v2_get_sync_status, v2_sync_push } from "../../actions/v2/sync";
import { useCurrentWorkingVessel } from "../../customHooks/useCurrentWorkingVessel";
import { getSyncState, setSyncState, subscribeSyncState } from "../../utils/syncState";

const IDLE_POLL_MS = 90_000;
const ACTIVE_POLL_MS = 30_000;

const SyncStatusBadge = () => {
  const { vessel } = useCurrentWorkingVessel();
  const [localPending, setLocalPending] = useState(0);
  const [serverPending, setServerPending] = useState(0);
  const [serverSyncState, setServerSyncState] = useState("idle");
  const [pushing, setPushing] = useState(false);
  const [apiReachable, setApiReachable] = useState(true);
  const [shared, setShared] = useState(getSyncState);
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  const pendingRef = useRef(0);

  const refresh = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;

    const local = await getQueueSummary();
    const count = local.count;
    setLocalPending(local.count);
    setSyncState({
      online,
      queueCount: local.count,
      failedCount: local.failedCount,
      state: !online ? "offline" : local.failedCount ? "failed" : local.count ? "queued" : getSyncState().state,
    });

    if (vessel?.vessel_id && online) {
      try {
        const liveUrl = `${window.RUNTIME_CONFIG?.API_BASE_URL ?? ""}/health/live`;
        const live = await fetch(liveUrl, { method: "GET", cache: "no-store" });
        setApiReachable(live.ok);
        setSyncState({ apiReachable: live.ok });
      } catch {
        setApiReachable(false);
        setSyncState({ apiReachable: false, state: count ? "queued" : "failed" });
      }

      try {
        const res = await v2_get_sync_status(vessel.vessel_id);
        if (res?.status) {
          const pendingOutbox = res.result?.pending_outbox ?? 0;
          const ledgerPending = res.result?.ledger_pending_sync ?? 0;
          const total = pendingOutbox + ledgerPending;
          setServerPending(total);
          pendingRef.current = count + total;
          setServerSyncState(
            res.result?.failed
              ? "FAILED"
              : pendingOutbox
                ? "PENDING"
                : "IDLE"
          );
          setApiReachable(true);
          setSyncState({
            apiReachable: true,
            queueCount: count + total,
            failedCount: res.result?.failed ? Math.max(1, local.failedCount) : local.failedCount,
            state: res.result?.failed ? "failed" : count + total ? "queued" : "synced",
            ...(count + total === 0 ? { lastSuccessfulAt: new Date().toISOString() } : {}),
          });
        }
      } catch {
        /* ignore when offline */
      }
    } else {
      pendingRef.current = count;
    }
  }, [vessel?.vessel_id, online]);

  useEffect(() => {
    const unsubscribe = subscribeSyncState(setShared);
    refresh();
    const onChange = () => refresh();
    window.addEventListener("elog-offline-queue-changed", onChange);
    window.addEventListener("elog-sync-flush", onChange);
    window.addEventListener("online", onChange);
    window.addEventListener("offline", onChange);
    document.addEventListener("visibilitychange", onChange);

    let timer = null;
    const schedule = () => {
      clearInterval(timer);
      const ms = pendingRef.current > 0 || !navigator.onLine || !apiReachable ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      timer = setInterval(refresh, ms);
    };
    schedule();
    const rescheduleOnRefresh = () => {
      setTimeout(schedule, 0);
    };
    window.addEventListener("elog-offline-queue-changed", rescheduleOnRefresh);

    return () => {
      window.removeEventListener("elog-offline-queue-changed", onChange);
      window.removeEventListener("elog-offline-queue-changed", rescheduleOnRefresh);
      window.removeEventListener("elog-sync-flush", onChange);
      window.removeEventListener("online", onChange);
      window.removeEventListener("offline", onChange);
      document.removeEventListener("visibilitychange", onChange);
      clearInterval(timer);
      unsubscribe();
    };
  }, [refresh, apiReachable]);

  const totalPending = localPending + serverPending;

  const lastSyncLabel = shared.lastSuccessfulAt
    ? new Date(shared.lastSuccessfulAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
    : "Not yet recorded";

  if (!online) {
    return (
      <span
        className="sync-status-badge sync-status-badge--queued"
        title={`Offline with ${localPending} change(s) safely queued. Last synchronized: ${lastSyncLabel}`}
      >
        <WifiOff size={14} /> Offline · queued {localPending}
      </span>
    );
  }

  if (!apiReachable) {
    return (
      <span
        className={`sync-status-badge ${shared.failedCount ? "sync-status-badge--failed" : "sync-status-badge--queued"}`}
        title={`Server connection unavailable. ${localPending} change(s) safely queued. Last synchronized: ${lastSyncLabel}`}
      >
        <CloudOff size={14} /> Server unavailable{localPending > 0 ? ` · ${localPending} queued` : ""}
      </span>
    );
  }

  if (totalPending === 0 && !shared.syncing && shared.failedCount === 0) {
    return (
      <span className="sync-status-badge sync-status-badge--synced" title={`Online and synchronized. Last synchronized: ${lastSyncLabel}`}>
        <CheckCircle2 size={14} /> Synchronized
      </span>
    );
  }

  const pushNow = async () => {
    if (!vessel?.vessel_id || pushing) return;
    setPushing(true);
    setSyncState({ syncing: true, retrying: true, state: "syncing" });
    try {
      await v2_sync_push(vessel.vessel_id);
    } finally {
      setPushing(false);
      setSyncState({ syncing: false, retrying: false });
      refresh();
    }
  };

  return (
    <button
      type="button"
      className={`sync-status-badge ${shared.failedCount ? "sync-status-badge--failed" : pushing || shared.syncing ? "sync-status-badge--syncing" : "sync-status-badge--queued"} btn btn-link p-0`}
      title={`${shared.failedCount ? "Synchronization failed" : "Online and synchronizing"} — local ${localPending}, server ${serverPending} (${serverSyncState}). Last synchronized: ${lastSyncLabel}. Click to retry.`}
      onClick={pushNow}
      disabled={pushing}
    >
      {pushing ? <RefreshCw size={14} className="spin" /> : <UploadCloud size={14} />}
      {shared.failedCount ? `Retry sync · ${totalPending}` : pushing || shared.syncing ? `Synchronizing · ${totalPending}` : `Queued · ${totalPending}`}
    </button>
  );
};

export default SyncStatusBadge;
